import { useState, useEffect } from "react";
import { createLiveTicket } from "@/services/zammadService";
import { getCurrentUser } from "@/services/entraIdService";
import AttachmentPicker from "@/components/feature/AttachmentPicker";

interface ServiceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceName: string;
  formUrl: string;
  departmentName: string;
  groupName?: string;
  metadata?: {
    appName?: string;
    categoryName?: string;
    classification?: string;
    priority?: string;
    slaHours?: number;
    description?: string;
  };
}

export default function ServiceRequestModal({ isOpen, onClose, serviceName, formUrl, departmentName, groupName, metadata }: ServiceRequestModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    asunto: "",
    departamento: "",
    descripcion: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | boolean | null>(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (isOpen) {
      setError(false);
      setSubmitting(false);
      setAttachments([]);
      const user = getCurrentUser();
      if (user) {
        setFormData({
          nombre: user.name || "",
          email: user.email || "",
          asunto: serviceName ? `${serviceName} - ${departmentName}` : "",
          departamento: user.department || "",
          descripcion: "",
        });
      } else {
        setFormData({
          nombre: "",
          email: "",
          asunto: serviceName ? `${serviceName} - ${departmentName}` : "",
          departamento: "",
          descripcion: "",
        });
      }
    }
  }, [isOpen, serviceName, departmentName]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Construir la ruta de subgrupo jerárquico de Zammad de forma dinámica para cada departamento
    let ticketGroup = groupName || departmentName;
    const lowerDept = departmentName.toLowerCase().trim();

    if (!groupName) {
      if (lowerDept.includes("ti") || lowerDept === "mesa de servicios ti") {
        if (metadata?.appName && metadata?.categoryName) {
          ticketGroup = `Servicio de TI::${metadata.appName}::${metadata.categoryName}`;
        } else if (metadata?.appName) {
          ticketGroup = `Servicio de TI::${metadata.appName}`;
        } else {
          ticketGroup = "Servicio de TI::Mesa de Ayuda";
        }
      } else if (lowerDept.includes("talento") || lowerDept.includes("humano")) {
        // Talento Humano está mapeado bajo TI en Zammad
        if (serviceName && serviceName !== "General") {
          ticketGroup = `Servicio de TI::Talento Humano::${serviceName}`;
        } else {
          ticketGroup = "Servicio de TI::Talento Humano";
        }
      } else if (lowerDept.includes("tecnología") || lowerDept.includes("tecnologia")) {
        // BID Tecnología tiene los servicios como grupos en Zammad (Nomisystem, SharePoint, etc.)
        ticketGroup = serviceName || departmentName;
      } else {
        // Para Contraloría, Administración, Servicios Generales, Diseño y Marketing, Área Legal, etc.
        if (serviceName && serviceName !== "General") {
          ticketGroup = `${departmentName}::${serviceName}`;
        }
      }
    }

    try {
      await createLiveTicket({
        title: formData.asunto.trim() || `${serviceName} - ${departmentName}`,
        customer: formData.email,
        group: ticketGroup,
        priority: metadata?.priority,
        type: metadata?.classification,
        attachments,
        body: `Nombre del solicitante: ${formData.nombre}
Departamento del solicitante: ${formData.departamento}
Servicio solicitado: ${serviceName}
Clasificación: ${metadata?.classification || "Solicitud"}
SLA: ${metadata?.slaHours ? `${metadata.slaHours} horas` : "N/A"}

Descripción del requerimiento:
${formData.descripcion}`,
      });

      setSubmitted(true);
      setFormData({
        nombre: "",
        email: "",
        asunto: "",
        departamento: "",
        descripcion: "",
      });
      setAttachments([]);

      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 2500);
    } catch (err) {
      console.error("Failed to submit ticket to Zammad:", err);
      setError(err instanceof Error ? err.message : "Error al crear la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  const getClassificationStyle = (c?: string) => {
    if (c === "Incidente") return "bg-accent-100 text-accent-800";
    if (c === "Problema") return "bg-primary-100 text-primary-800";
    return "bg-secondary-100 text-secondary-800";
  };

  const getPriorityStyle = (p?: string) => {
    if (p === "Alta" || p === "Crítica") return "text-accent-700";
    if (p === "Media") return "text-secondary-700";
    return "text-foreground-500";
  };

  const slaLabel = (h?: number) => {
    if (!h) return "";
    if (h < 24) return `${h} hrs`;
    if (h === 24) return "1 día";
    if (h < 120) return `${Math.round(h / 24)} días`;
    return `${Math.round(h / 24)} días`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl overflow-hidden animate-[fadeIn_0.2s_ease-out] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-background-200/70">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-heading font-semibold text-foreground-950">
                Solicitar Servicio
              </h3>
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-1.5">
                {(() => {
                  const rawPath = groupName || "";
                  const parts = rawPath.includes("::")
                    ? rawPath.split("::").map((p) => p.trim())
                    : [departmentName, serviceName].filter(Boolean);

                  return parts.map((part, idx) => {
                    const isLast = idx === parts.length - 1;
                    if (isLast) {
                      return (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold"
                        >
                          <i className="ri-price-tag-3-line text-[10px]"></i>
                          {part}
                        </span>
                      );
                    }
                    return (
                      <span key={idx} className="flex items-center gap-1.5 text-xs text-foreground-500 font-medium">
                        <span>{part}</span>
                        <span className="text-foreground-300 text-xs">&rsaquo;</span>
                      </span>
                    );
                  });
                })()}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors flex-shrink-0 ml-2"
            >
              <i className="ri-close-line text-foreground-600"></i>
            </button>
          </div>

          {/* Metadata badges */}
          {metadata && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {metadata.classification && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getClassificationStyle(metadata.classification)}`}>
                  <i className="ri-flag-line text-[10px]"></i>
                  {metadata.classification}
                </span>
              )}
              {metadata.priority && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-100 ${getPriorityStyle(metadata.priority)}`}>
                  <i className="ri-speed-line text-[10px]"></i>
                  Prioridad {metadata.priority}
                </span>
              )}
              {metadata.slaHours && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-600">
                  <i className="ri-time-line text-[10px]"></i>
                  SLA: {slaLabel(metadata.slaHours)}
                </span>
              )}
            </div>
          )}

          {metadata?.description && (
            <div className="mt-3 p-2.5 rounded-lg bg-background-100/80 border border-background-200/60">
              <p className="text-xs text-foreground-600 leading-relaxed">
                <i className="ri-information-line text-foreground-400 mr-1"></i>
                {metadata.description}
              </p>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {submitted ? (
            <div className="px-6 py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-accent-600 text-2xl"></i>
              </div>
              <h4 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                Solicitud enviada exitosamente
              </h4>
              <p className="text-sm text-foreground-600">
                El equipo de {departmentName} recibió tu solicitud para <strong>{serviceName}</strong>. Te contactaremos pronto.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-6 py-5" data-readdy-form="">
              <button type="submit" className="hidden" aria-hidden="true">Submit</button>
              {error && (
                <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2.5 animate-[fadeIn_0.2s_ease-out]">
                  <i className="ri-error-warning-line text-base text-red-500 flex-shrink-0"></i>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold block">Error al crear la solicitud</span>
                    <p className="mt-0.5 text-red-600/90 break-words">{typeof error === "string" ? error : "Ocurrió un inconveniente al enviar la solicitud."}</p>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label htmlFor="nombre" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    readOnly
                    title="El ticket se asociará al usuario autenticado"
                    required
                    className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-600 placeholder:text-foreground-400 focus:outline-none"
                    placeholder="tu@bidgroup.com"
                  />
                </div>
                <div>
                  <label htmlFor="asunto" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Asunto / Título de la solicitud *
                  </label>
                  <input
                    type="text"
                    id="asunto"
                    name="asunto"
                    value={formData.asunto}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                    placeholder="Ej. Solicitud de nuevo usuario o falla de sistema"
                  />
                </div>
                <div>
                  <label htmlFor="departamento" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Departamento / Área
                  </label>
                  <input
                    type="text"
                    id="departamento"
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                    placeholder="Tu área o unidad de negocio"
                  />
                </div>
                <div>
                  <label htmlFor="descripcion" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Descripción de la solicitud *
                  </label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
                    placeholder="Describe tu solicitud con el mayor detalle posible"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {formData.descripcion.length}/500
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Archivos adjuntos
                  </label>
                  <AttachmentPicker
                    files={attachments}
                    onChange={setAttachments}
                    disabled={submitting}
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer actions */}
        {!submitted && (
          <div className="flex-shrink-0 px-6 py-4 border-t border-background-200/70">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-5 py-2.5 rounded-full border border-background-300 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-100 transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all flex items-center justify-center gap-2 disabled:bg-primary-300 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <i className="ri-send-plane-line text-sm"></i>
                )}
                {submitting ? "Enviando..." : "Enviar Solicitud"}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
